/**
 * Emails (Snov) — textos y cuentas de las pantallas. Puro (sin React): lo prueba logic/emails.test.ts.
 */

import type { ComercioCorto, Conteos, EstadoCampana, EstadoPersona, FilaHora, FiltroPersona, Seguimiento } from "../types";

export const R_EMAILS = "/activa/emails";

export const TEXTO_ESTADO_CAMPANA: Record<EstadoCampana, { texto: string; cls: string }> = {
  activa: { texto: "Enviando", cls: "bg-emerald-50 text-emerald-700" },
  pausada: { texto: "Pausada", cls: "bg-amber-50 text-amber-700" },
  borrador: { texto: "Borrador", cls: "bg-slate-100 text-slate-600" },
  terminada: { texto: "Terminada", cls: "bg-sky-50 text-sky-700" },
  otro: { texto: "Otro estado", cls: "bg-slate-100 text-slate-600" },
};

/** Una activa que ya no tiene a nadie pendiente (`progreso.faltan` en 0). */
export const MANDO_A_TODOS = { texto: "Mandó a todos", cls: "bg-sky-50 text-sky-700" };

export const TEXTO_ESTADO_PERSONA: Record<EstadoPersona, { texto: string; cls: string }> = {
  baja: { texto: "Se dio de baja", cls: "bg-red-50 text-red-700" },
  pidio_baja: { texto: "Pidió la baja", cls: "bg-red-50 text-red-700" },
  respondio: { texto: "Respondió", cls: "bg-violet-50 text-violet-700" },
  clic: { texto: "Hizo clic", cls: "bg-emerald-50 text-emerald-700" },
  abrio: { texto: "Abrió", cls: "bg-sky-50 text-sky-700" },
  entregado: { texto: "Le llegó", cls: "bg-slate-100 text-slate-600" },
  reboto: { texto: "Rebotó", cls: "bg-amber-50 text-amber-800" },
};

/**
 * Los filtros de la tabla de personas, en el orden en que se muestran. «Sin interacción» no es «no contestó»: es no haber
 * hecho NADA (ni abrir, ni clic, ni contestar). `soloSiHay`: el chip aparece sólo si alguien entra (o si está elegido).
 */
export const FILTROS_PERSONA: { valor: FiltroPersona; texto: string; titulo?: string; soloSiHay?: boolean }[] = [
  { valor: "todas", texto: "Todas" },
  { valor: "respondieron", texto: "Respondieron" },
  { valor: "clic", texto: "Hicieron clic" },
  { valor: "abrieron", texto: "Abrieron" },
  { valor: "sin_respuesta", texto: "Sin interacción", titulo: "No abrieron, no hicieron clic, no contestaron, no rebotaron ni se dieron de baja" },
  { valor: "rebotaron", texto: "Rebotaron" },
  { valor: "bajas", texto: "Bajas", titulo: "Con el link de baja de Snov o contestando que no quieren recibir más" },
  {
    valor: "pidieron_baja", texto: "Pidieron la baja contestando", soloSiHay: true,
    titulo: "Snov no las dio de baja: si nadie las pasa a «No enviar», la próxima campaña les vuelve a escribir",
  },
];

export const esFiltroPersona = (v: string | null): v is FiltroPersona => FILTROS_PERSONA.some((f) => f.valor === v);

/** «12,3 %», o «—» sin dato. */
export function tasaTexto(t: number | null | undefined): string {
  if (t === null || t === undefined || !Number.isFinite(Number(t))) return "—";
  return `${(Math.round(Number(t) * 1000) / 10).toLocaleString("es-AR")} %`;
}

export interface Cambio {
  texto: string;
  /** hacia dónde se movió: 1 subió, -1 bajó, 0 igual (el color lo decide quien lo muestra) */
  signo: -1 | 0 | 1;
}

/** Cómo se movió una tasa, en puntos: «+1,5 pp», «-0,3 pp», «igual»; null si en uno de los dos no hay dato. */
export function cambioEnPuntos(ahora: number | null | undefined, antes: number | null | undefined): Cambio | null {
  if (ahora === null || ahora === undefined || antes === null || antes === undefined || !Number.isFinite(ahora) || !Number.isFinite(antes)) return null;
  const pp = Math.round((ahora - antes) * 1000) / 10;
  if (pp === 0) return { texto: "igual", signo: 0 };
  return { texto: `${pp > 0 ? "+" : "-"}${Math.abs(pp).toLocaleString("es-AR")} pp`, signo: pp > 0 ? 1 : -1 };
}

/** Cómo se movió una cantidad: «+50 %», «-20 %», «igual»; desde 0, «antes, ninguno». */
export function cambioRelativo(ahora: number, antes: number): Cambio {
  if (ahora === antes) return { texto: "igual", signo: 0 };
  if (!(antes > 0)) return { texto: "antes, ninguno", signo: 1 };
  const pct = Math.round(((ahora - antes) / antes) * 100);
  if (pct === 0) return { texto: "casi igual", signo: 0 };
  return { texto: `${pct > 0 ? "+" : "-"}${Math.abs(pct).toLocaleString("es-AR")} %`, signo: pct > 0 ? 1 : -1 };
}

/** 0–100 para la barrita de un StatTile, o undefined sin dato. */
export const metro = (t: number | null | undefined): number | undefined => (t === null || t === undefined || !Number.isFinite(t) ? undefined : t * 100);

/**
 * El asunto como lo lee una persona: las variables de Snov («{{first_name}}») pasan a «‹nombre›». El resto queda
 * igual (es lo que escribió el equipo).
 */
export function asuntoLegible(asunto: string): string {
  return String(asunto || "")
    .replace(/\{\{\s*first[_ ]?name\s*\}\}/gi, "‹nombre›")
    .replace(/\{\{\s*last[_ ]?name\s*\}\}/gi, "‹apellido›")
    .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_x, v: string) => `‹${v.toLowerCase()}›`)
    .trim();
}

/** El nombre de un comercio para mostrar: su marca, o el identificador si no tiene. */
export function nombreComercio(brandId: string | null | undefined, comercios: readonly ComercioCorto[]): string {
  if (!brandId) return "";
  const c = comercios.find((x) => x.brandId === brandId);
  return c?.marca?.trim() || brandId;
}

/**
 * Lo que mide una campaña, dicho en una línea. Snov apaga aperturas o clics por campaña; sin seguimiento, el número
 * no existe (no es 0).
 */
export function textoSeguimiento(s: Seguimiento | null | undefined): string | null {
  if (!s) return null;
  if (s.aperturas === false && s.clics === false) return "Esta campaña no mide aperturas ni clics (está apagado en Snov).";
  if (s.aperturas === false) return "Esta campaña no mide aperturas (está apagado en Snov): se cuentan los clics y las respuestas.";
  if (s.clics === false) return "Esta campaña no mide clics (está apagado en Snov).";
  return null;
}

/**
 * La franja de dos horas en que más interactúan (aperturas, clics y respuestas juntas), o null con menos de 10
 * interacciones: con tan poco, «la mejor hora» sería ruido.
 */
export function mejorFranja(porHora: readonly FilaHora[] | null | undefined): { desde: number; hasta: number; pct: number } | null {
  const filas = Array.from({ length: 24 }, (_x, h) => porHora?.find((f) => f.hora === h) || { hora: h, aperturas: 0, clics: 0, respuestas: 0 });
  const total = filas.reduce((s, f) => s + f.aperturas + f.clics + f.respuestas, 0);
  if (total < 10) return null;
  let mejor = { desde: 0, suma: -1 };
  for (let h = 0; h < 24; h++) {
    const a = filas[h];
    const b = filas[(h + 1) % 24];
    const suma = a.aperturas + a.clics + a.respuestas + b.aperturas + b.clics + b.respuestas;
    if (suma > mejor.suma) mejor = { desde: h, suma };
  }
  return { desde: mejor.desde, hasta: (mejor.desde + 2) % 24, pct: mejor.suma / total };
}

/** «20 a 22 h», «23 a 1 h». */
export const textoFranja = (f: { desde: number; hasta: number }) => `${f.desde} a ${f.hasta} h`;

/** «07/10» de un instante, en la hora del negocio. */
export function diaMes(iso: string | null | undefined, tz = "America/Argentina/Buenos_Aires"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { timeZone: tz, day: "2-digit", month: "2-digit" });
}

/** «07/10 21:59» de un instante, en la hora del negocio. */
export function diaMesHora(iso: string | null | undefined, tz = "America/Argentina/Buenos_Aires"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${diaMes(iso, tz)} ${d.toLocaleTimeString("es-AR", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}`;
}

/**
 * Cuándo salió una campaña, en una línea: «07/10 → 10/10», «el 07/10», o «Todavía no salió».
 */
export function textoSalida(primerEnvio: string | null, ultimoEnvio: string | null): string {
  if (!primerEnvio) return "Todavía no salió";
  const a = diaMes(primerEnvio);
  const b = diaMes(ultimoEnvio || primerEnvio);
  return a === b ? `El ${a}` : `${a} → ${b}`;
}

/** ¿Hay algo para mostrar en los números? Una campaña sin conteos todavía no se leyó (o no salió). */
export const conDatos = (c: Conteos | null | undefined): c is Conteos => !!c && c.enviados > 0;

/** Baja un texto como archivo (el CSV de personas). */
export function descargarTexto(nombre: string, contenido: string, tipo = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
